import React, { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import BookDetailsPage from "./pages/BookDetailsPage";
import CartPage from "./pages/CartPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import UserRevenueManagement from "./pages/UserRevenueManagement";
import CouponManagement from "./pages/CouponManagement";
import DiscountManagement from "./pages/DiscountManagement";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import PayoutManagement from "./pages/PayoutManagement";
import PlatformAnalytics from "./pages/PlatformAnalytics";
import EditProfilePage from "./pages/EditProfilePage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ReadingPreferencesPage from "./pages/ReadingPreferencesPage";
import VendorManagement from "./pages/VendorManagement";
import BulkBookUpload from "./pages/BulkBookUpload";
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ContactPage from "./pages/ContactPage";
import TransactionsPage from "./pages/TransactionsPage";
import RentalActionPage from "./pages/RentalActionPage";
import MyCopiesPage from "./pages/MyCopiesPage";
import NotificationsPage from "./pages/NotificationsPage";
import CatalogBrowsePage from "./pages/CatalogBrowsePage";
import CatalogBookDetailPage from "./pages/CatalogBookDetailPage";
import OperationsOrdersPage from "./pages/OperationsOrdersPage";
import ScholarshipEligibilityPage from "./pages/ScholarshipEligibilityPage";
import ScholarshipApplyPage from "./pages/ScholarshipApplyPage";
import ScholarshipAttestPage from './pages/ScholarshipAttestPage';
import ScholarshipStatusPage from "./pages/ScholarshipStatusPage";
import ScholarshipUtilizationPage from "./pages/ScholarshipUtilizationPage";
import ScholarshipAdminPage from "./pages/ScholarshipAdminPage";
import { Toaster, toast } from "@/components/ui/sonner";
import { isBookOwner, isPlatformAdmin, isEmployee } from "./lib/roles";

// Suppress ResizeObserver loop error (benign browser warning)
const resizeObserverErr = window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded') {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, newToken = null) => {
      failedQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(newToken);
      });
      failedQueue = [];
    };

    // MBR-109: always send the freshest token (localStorage), not a stale closure
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const latest = localStorage.getItem("token");
      if (latest) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${latest}`;
      }
      return config;
    });

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }
        if (originalRequest.url?.includes("/auth/refresh")) {
          logout();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const currentToken = tokenRef.current || localStorage.getItem("token");
          const response = await axios.post(
            `${API}/auth/refresh`,
            {},
            { headers: { Authorization: `Bearer ${currentToken}` } }
          );
          const newToken = response.data.token;
          localStorage.setItem("token", newToken);
          setToken(newToken);
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          logout();
          toast.error("Session expired. Please log in again.");
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (e) {
      console.error("Failed to fetch user", e);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const login = (newToken, userData, options = {}) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);

    // Welcome banner on homepage (MBR-033) — not a toast popup
    if (options.showWelcome !== false) {
      const welcomeMessages = {
        customer: `Welcome back, ${userData.name}! Ready to discover your next favorite book?`,
        admin: `Hello ${userData.name}! Your book collection awaits.`,
        employee: `Welcome back, ${userData.name}!`,
        super_admin: `Welcome back, ${userData.name}! Your Executive Leader dashboard is ready.`,
        founder: `Welcome back, ${userData.name}! Full platform access is ready.`,
      };
      const message = welcomeMessages[userData.role] || `Welcome back, ${userData.name}!`;
      sessionStorage.setItem("welcomeBanner", message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/rental-action" element={<RentalActionPage />} />
            <Route path="/refund" element={<RefundPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/plans" element={<Navigate to="/" replace />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/reading-preferences" element={user ? <ReadingPreferencesPage /> : <Navigate to="/login" />} />
            <Route path="/books/:id" element={<BookDetailsPage />} />
            <Route path="/cart" element={user ? <CartPage /> : <Navigate to="/login" />} />
            <Route path="/checkout" element={user ? <CheckoutPage /> : <Navigate to="/login" />} />
            <Route path="/order-confirmation/:orderId" element={user ? <OrderConfirmationPage /> : <Navigate to="/login" />} />
            <Route
              path="/customer-dashboard"
              element={user ? <CustomerDashboard /> : <Navigate to="/login" />}
            />
            <Route 
              path="/admin" 
              element={user && isBookOwner(user.role) ? <AdminDashboard /> : <Navigate to="/login" />} 
            />
            <Route
              path="/operations"
              element={
                user && (isEmployee(user.role) || isPlatformAdmin(user.role))
                  ? <OperationsOrdersPage />
                  : <Navigate to={user ? "/" : "/login?redirect=/operations"} replace />
              }
            />
            <Route 
              path="/super-admin" 
              element={user && isPlatformAdmin(user.role) ? <SuperAdminDashboard /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/user-revenue-management" 
              element={user && isPlatformAdmin(user.role) ? <UserRevenueManagement /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/coupon-management" 
              element={user && isPlatformAdmin(user.role) ? <CouponManagement /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/discount-management" 
              element={user && isPlatformAdmin(user.role) ? <DiscountManagement /> : <Navigate to="/login" />} 
            />
            <Route path="/subscription-plan-management" element={<Navigate to="/super-admin" replace />} />
            <Route path="/book-value-rules" element={<Navigate to="/super-admin" replace />} />
            <Route 
              path="/payout-management" 
              element={user && isPlatformAdmin(user.role) ? <PayoutManagement /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/platform-analytics" 
              element={user && isPlatformAdmin(user.role) ? <PlatformAnalytics /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/vendor-management" 
              element={user && isPlatformAdmin(user.role) ? <VendorManagement /> : <Navigate to="/login" />} 
            />
            <Route 
              path="/bulk-book-upload" 
              element={user && isBookOwner(user.role) ? <BulkBookUpload /> : <Navigate to={user ? "/" : "/login?redirect=/bulk-book-upload"} replace />} 
            />
            <Route 
              path="/admin/bulk-upload" 
              element={user && isBookOwner(user.role) ? <BulkBookUpload /> : <Navigate to={user ? "/" : "/login?redirect=/admin/bulk-upload"} replace />} 
            />
            <Route 
              path="/admin/bulk-book-upload" 
              element={user && isBookOwner(user.role) ? <BulkBookUpload /> : <Navigate to={user ? "/" : "/login?redirect=/admin/bulk-upload"} replace />} 
            />
            <Route 
              path="/bulk-upload" 
              element={user && isBookOwner(user.role) ? <BulkBookUpload /> : <Navigate to={user ? "/" : "/login?redirect=/bulk-upload"} replace />} 
            />
            <Route path="/edit-profile" element={user ? <EditProfilePage /> : <Navigate to="/login" />} />
            <Route path="/transactions" element={user ? <TransactionsPage /> : <Navigate to="/login" />} />
            <Route path="/notifications" element={user ? <NotificationsPage /> : <Navigate to="/login" />} />
            <Route 
              path="/order-success/:orderId" 
              element={user ? <OrderSuccessPage /> : <Navigate to="/login" />} 
            />
            {/* Two-layer catalog routes */}
            <Route path="/catalog" element={<CatalogBrowsePage />} />
            <Route path="/catalog/:bookId" element={<CatalogBookDetailPage />} />
            <Route
              path="/add-book"
              element={
                user && isBookOwner(user.role)
                  ? <Navigate to="/admin?add=1" replace />
                  : <Navigate to={user ? "/" : "/login"} replace />
              }
            />
            <Route 
              path="/my-copies" 
              element={user ? <MyCopiesPage /> : <Navigate to="/login" />} 
            />
            <Route path="/scholarship/eligibility" element={<ScholarshipEligibilityPage />} />
            <Route path="/scholarship/status" element={<ScholarshipStatusPage />} />
            <Route
              path="/scholarship/apply"
              element={user ? <ScholarshipApplyPage /> : <Navigate to="/login?redirect=/scholarship/apply" replace />}
            />
            <Route path="/scholarship/attest" element={<ScholarshipAttestPage />} />
            <Route
              path="/scholarship/utilization"
              element={user ? <ScholarshipUtilizationPage /> : <Navigate to="/login?redirect=/scholarship/utilization" replace />}
            />
            <Route
              path="/scholarship/admin"
              element={
                user && (isEmployee(user.role) || isPlatformAdmin(user.role))
                  ? <ScholarshipAdminPage />
                  : <Navigate to={user ? "/" : "/login?redirect=/scholarship/admin"} replace />
              }
            />
            {/* Catch-all route to avoid blank pages for unmatched paths */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
